import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { MobileSector, NewFlightItemVm } from '@app/entities/flight-results';
import { FlightItineraryAbstractComponent } from '@shared/flight-itinerary/abstracts/flight-itinerary.abstract';
import { FlightClassTypes } from '@shared/base-search-bar/entities/flight-class-types';
import { routeDetail } from '@flight-results/flight-results-routing.constants';
import { TripTypes } from '@app/entities/trip-types';
import { TravelDetailProvider } from '@shared/flight-itinerary/entities/travel-detail-provider';
import { LuggageService } from '@shared/luggage/services/luggage.service';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { FlightScaleTableSize } from '@design-system/components/organisms/flight-scale-table/entities/flight-scale-table.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { flightItemMapper } from '@flight-results/mappers/flights.mapper';
import { TypographyColor } from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-simple-travel-summary',
  templateUrl: './simple-travel-summary.component.html',
  styleUrls: ['./simple-travel-summary.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SimpleTravelSummaryComponent extends FlightItineraryAbstractComponent {

  @Input() options: MobileSector[];
  @Input() type: number;
  @Input() travelClass: FlightClassTypes;
  @Input() provider: string;
  @Input() price: string;
  @Input() summaryProvider: TravelDetailProvider;
  @Input() source: EVENT_SOURCE | string;
  @Input() isPackages: boolean;
  @Input() isInternational: boolean;
  @Input() optionId: number;
  @Input() insideCard = false;

  @Output() showDetailEvent: EventEmitter<void> = new EventEmitter<void>();

  public readonly flightScaleTableSize = FlightScaleTableSize.MD;

  public readonly iconChevronRight = Icon.ChevronRight;
  public readonly fontColor = TypographyColor;

  constructor(
    private readonly router: Router,
    private readonly urlRoute: ActivatedRoute,
    private readonly luggageService: LuggageService
  ) {
    super();
  }

  public routeDetail: string = routeDetail;

  get isBookingProvider(): boolean {
    return this.summaryProvider === TravelDetailProvider.BOOKING_FLIGHT ||
      this.summaryProvider === TravelDetailProvider.BOOKING_BUSES ||
      this.summaryProvider === TravelDetailProvider.BOOKING_PACKAGE_AIR ||
      this.summaryProvider === TravelDetailProvider.RESUME_WITH_ASSISTANCE ||
      this.summaryProvider === TravelDetailProvider.RESUME_WITH_EXTRA;
  }

  get isPackageResumeProvider(): boolean {
    return this.summaryProvider === TravelDetailProvider.PACKAGE_RESUME;
  }

  get isUserHistory(): boolean {
    return this.summaryProvider === TravelDetailProvider.USER_HISTORY;
  }

  get openDetailIsEnabled(): boolean {
    return !this.isUserHistory && !this.isBookingProvider && !this.isPackageResumeProvider;
  }

  get sector(): NewFlightItemVm {
    const sectorSelected = this.flightItems?.find(option => option.SectorNumber === this.type);
    return sectorSelected;
  }

  get flightItems(): NewFlightItemVm[] {
    return this.options.map(sector => {
      return flightItemMapper(sector, this.optionId); 
    })
  }

  get tripType(): string {
    return !this.isReturn ? 'FLIGHT_RESULTS_MODULE.ITINERARY_LIST_SECTION.DEPARTURE_TRIP' :
      'FLIGHT_RESULTS_MODULE.ITINERARY_LIST_SECTION.RETURN_TRIP';
  }

  get isReturn(): boolean {
    return this.type === TripTypes.RETURN;
  }

  get isBus(): boolean {
    return this.summaryProvider === TravelDetailProvider.BUS_RESULTS ||
      this.summaryProvider === TravelDetailProvider.BOOKING_BUSES;
  }

  public disableBackpackLuggage(airlineCode: string): boolean {
    return this.luggageService.disableBackpackLuggage(airlineCode);
  }

  public includesLuggageWarehouse(baggage: number): boolean {
    return this.luggageService.includesLuggageWarehouse(baggage);
  }

  public openDetailByCard(): void {
    if (this.openDetailIsEnabled || this.isBookingProvider) {
      this.openDetail();
    }
  }

  public openDetail(): void {
    if (!this.isPackageResumeProvider) {
      if (this.isBookingProvider) {
        this.showDetailEvent.emit();
      } else if (this.isBus) {
        this.router.navigate([
          { outlets: { 'detail': [routeDetail] } }],
        {
          state: {
            routes: this.flightItems,
            busClass: this.travelClass,
            provider: this.provider,
            price: this.price,
            routeSectorNumberSelected: this.sector.SectorNumber,
            isInternational: this.isInternational
          },
          relativeTo: this.urlRoute, skipLocationChange: true
        }
        );
      } else {
        this.router.navigate([
          { outlets: { 'detail': [routeDetail] } }],
        {
          queryParams: { source: this.source },
          state: {
            options: this.flightItems,
            flightClass: this.travelClass,
            provider: this.provider,
            price: this.price,
            sectorNumberSelected: this.sector.SectorNumber,
            optionId: this.optionId,
            isPackageView: this.isPackages,
            isInternational: this.isInternational
          },
          relativeTo: this.urlRoute, skipLocationChange: true
        }
        );
      }
    }
  }
}
